// Convenções visuais do funil comercial (status/etapas) usadas no Mapa Inteligente
// e demais telas do módulo GeoIntelligence. Cores em hex para uso em pins Leaflet.

export interface LeadVisualInput {
    stage: string;
    status: string;
    congelado?: boolean;
}

export const STATUS_LABELS: Record<string, string> = {
    em_andamento: 'Em andamento',
    ganho: 'Ganho',
    perdido: 'Perdido',
    em_pausa: 'Em pausa',
    simulacao_realizada: 'Simulação realizada',
    termo_enviado: 'Termo enviado',
    termo_assinado: 'Termo assinado',
    termo_analise: 'Termo em análise',
    termo_aprovado: 'Termo aprovado',
    termo_reprovado: 'Termo reprovado',
};

// Cores alinhadas aos badges do Kanban (tailwind 500)
const STATUS_HEX: Record<string, string> = {
    simulacao_realizada: '#a855f7', // purple-500
    termo_enviado: '#eab308',       // yellow-500
    termo_analise: '#f97316',       // orange-500
    termo_aprovado: '#22c55e',      // green-500
    termo_assinado: '#14b8a6',      // teal-500
    termo_reprovado: '#ef4444',     // red-500
    em_pausa: '#facc15',            // yellow-400
    ganho: '#22c55e',
};

const STAGE_HEX: Array<{ test: RegExp; color: string }> = [
    { test: /prospec/i, color: '#3b82f6' },   // blue-500
    { test: /qualifica/i, color: '#0ea5e9' }, // sky-500
    { test: /negocia/i, color: '#6366f1' },   // indigo-500
    { test: /termo/i, color: '#eab308' },     // yellow-500
];

export const CONGELADO_HEX = '#94a3b8'; // slate-400

/** Cor do pin/badge do lead no mapa: status granular vence; em_andamento cai na etapa. */
export const getLeadMapColor = (lead: LeadVisualInput): string => {
    if (lead.congelado) return CONGELADO_HEX;
    if (STATUS_HEX[lead.status]) return STATUS_HEX[lead.status];
    const byStage = STAGE_HEX.find(s => s.test.test(lead.stage || ''));
    return byStage ? byStage.color : '#3b82f6';
};

/** Rótulo da posição do lead no funil: status granular ou nome da etapa. */
export const getLeadMapLabel = (lead: LeadVisualInput): string => {
    if (lead.status && lead.status !== 'em_andamento' && STATUS_LABELS[lead.status]) {
        return STATUS_LABELS[lead.status];
    }
    return lead.stage || STATUS_LABELS[lead.status] || lead.status;
};

/** Legenda do mapa (ordem do funil). */
export const MAP_LEGEND: Array<{ label: string; color: string }> = [
    { label: 'Prospecção', color: '#3b82f6' },
    { label: 'Qualificação', color: '#0ea5e9' },
    { label: 'Em negociação', color: '#6366f1' },
    { label: 'Simulação realizada', color: '#a855f7' },
    { label: 'Termo enviado', color: '#eab308' },
    { label: 'Termo em análise', color: '#f97316' },
    { label: 'Termo aprovado', color: '#22c55e' },
    { label: 'Termo assinado', color: '#14b8a6' },
    { label: 'Termo reprovado', color: '#ef4444' },
    { label: 'Lead congelado', color: CONGELADO_HEX },
];

/** Opções do filtro de status do Mapa Inteligente. */
export const MAP_STATUS_FILTERS: Array<{ value: string; label: string }> = [
    { value: 'todos', label: 'Todos os Status' },
    { value: 'stage:prospec', label: 'Prospecção' },
    { value: 'stage:qualifica', label: 'Qualificação' },
    { value: 'stage:negocia', label: 'Em negociação' },
    { value: 'simulacao_realizada', label: 'Simulação realizada' },
    { value: 'termo_enviado', label: 'Termo enviado' },
    { value: 'termo_analise', label: 'Termo em análise' },
    { value: 'termo_aprovado', label: 'Termo aprovado' },
    { value: 'termo_assinado', label: 'Termo assinado' },
    { value: 'termo_reprovado', label: 'Termo reprovado' },
    { value: 'em_pausa', label: 'Em pausa' },
    { value: 'congelado', label: '❄ Congelados' },
];

export const leadMatchesStatusFilter = (lead: LeadVisualInput, filter: string): boolean => {
    if (!filter || filter === 'todos') return true;
    if (filter === 'congelado') return !!lead.congelado;
    if (filter.startsWith('stage:')) {
        const term = filter.slice(6);
        return lead.status === 'em_andamento' && new RegExp(term, 'i').test(lead.stage || '');
    }
    return lead.status === filter;
};
